/**
 * Company-field visibility — the ONE predicate for `organizations.settings.customer_type`.
 *
 * A tenant that only sells to persoane fizice was still asked for CUI / SRL /
 * registration number on every lead and client. This module decides, per entity,
 * which keys exist *because a customer might be a company* — every lead surface,
 * client surface, import template and variable picker gates through it, so a new
 * call site inherits the gate instead of re-deriving `t === 'individuals'`.
 *
 * HIDE ONLY. Nothing here nulls a column, and ingest is never gated: a Woo / Meta /
 * CSV payload carrying a company value is still stored. Flipping the setting back
 * to 'both' restores every field because no data was ever removed.
 *
 * Pure by design — no React, no DB, no network — so the contract three other
 * surfaces code against is unit-testable on its own.
 *
 * Related: [[knowledge/db/supabase-patterns]] [[knowledge/auth-security]]
 */

/** What an org sells to. `'both'` is today's behaviour and the safe degrade target. */
export type CustomerType = 'individuals' | 'companies' | 'both';

export const DEFAULT_CUSTOMER_TYPE: CustomerType = 'both';

/** Every valid value, in the order the onboarding/settings pickers render them. */
export const CUSTOMER_TYPES = [
  'individuals',
  'companies',
  'both',
] as const satisfies readonly CustomerType[];

/** The two record kinds that carry company identity. */
export type CompanyFieldEntity = 'lead' | 'client';

/**
 * The field-config and import code speaks plural (`'leads' | 'clients'`, the
 * `EntityType` in features/leads/import/schemas); the registries here are singular.
 * One mapper so no caller writes its own ternary and gets it backwards.
 */
export function companyFieldEntityFor(entityType: 'leads' | 'clients'): CompanyFieldEntity {
  return entityType === 'clients' ? 'client' : 'lead';
}

/** DB column keys that only exist because a customer might be a company. */
export const LEAD_COMPANY_FIELD_KEYS = [
  'company',
  'cui',
  'tax_id',
  'registration_number',
  'website',
] as const;

export const CLIENT_COMPANY_FIELD_KEYS = [
  'company_name',
  'job_title',
  'website',
  'cui',
  'tax_id',
  'registration_number',
] as const;

const COMPANY_FIELD_KEYS: Record<CompanyFieldEntity, readonly string[]> = {
  lead: LEAD_COMPANY_FIELD_KEYS,
  client: CLIENT_COMPANY_FIELD_KEYS,
};

/**
 * Coerce anything (JSONB blob value, form input, URL param) to a CustomerType.
 *
 * Degrades to `'both'` on absent/garbage input rather than to a blank form: a bad
 * deploy or a hand-edited settings row must land on today's behaviour, never on a
 * record with its company identity silently missing.
 */
export function parseCustomerType(raw: unknown): CustomerType {
  return typeof raw === 'string' && (CUSTOMER_TYPES as readonly string[]).includes(raw)
    ? (raw as CustomerType)
    : DEFAULT_CUSTOMER_TYPE;
}

/**
 * THE single predicate. Nothing may re-derive this from a raw string comparison —
 * a second `t === 'individuals'` somewhere is how the class comes back.
 */
export function hidesCompanyFields(t: CustomerType | undefined | null): boolean {
  return parseCustomerType(t) === 'individuals';
}

/**
 * Which field IDENTIFIES a client for this org: the person's name, the company, or the
 * website. Stored at `organizations.settings.client_identity`; default 'person' (today's
 * behaviour). Decides the required field of the lead→client convert dialog and which
 * field a client is titled by (see getClientDisplayName).
 */
export const CLIENT_IDENTITIES = ['person', 'company', 'website'] as const;
export type ClientIdentity = (typeof CLIENT_IDENTITIES)[number];

export const DEFAULT_CLIENT_IDENTITY: ClientIdentity = 'person';

/** Coerce anything (JSONB value, wire payload) to a ClientIdentity; garbage → 'person'. */
export function parseClientIdentity(raw: unknown): ClientIdentity {
  return typeof raw === 'string' && (CLIENT_IDENTITIES as readonly string[]).includes(raw)
    ? (raw as ClientIdentity)
    : DEFAULT_CLIENT_IDENTITY;
}

/**
 * The identity actually in force: an org that sells only to individuals has no company or
 * website identity (those fields are hidden), so it is forced back to 'person' no matter
 * what is stored. Null/unknown → 'person'.
 */
export function effectiveClientIdentity(
  identity: ClientIdentity | null | undefined,
  customerType?: CustomerType | null
): ClientIdentity {
  if (hidesCompanyFields(customerType)) return 'person';
  return parseClientIdentity(identity);
}

/** The company keys for one entity — read-only, so no caller can mutate the registry. */
export function companyFieldKeysFor(entity: CompanyFieldEntity): readonly string[] {
  return COMPANY_FIELD_KEYS[entity];
}

/** Whether a key is company identity for this entity (regardless of the org setting). */
export function isCompanyFieldKey(key: string, entity: CompanyFieldEntity): boolean {
  return COMPANY_FIELD_KEYS[entity].includes(key);
}

/**
 * Drop company keys from any key list — columns, onboarding presets, import
 * templates, variable pickers. Returns the input order untouched when the org
 * still sells to companies, so a caller can use it unconditionally.
 */
export function filterCompanyFieldKeys<T extends string>(
  keys: readonly T[],
  t: CustomerType | undefined | null,
  entity: CompanyFieldEntity
): T[] {
  if (!hidesCompanyFields(t)) return [...keys];
  const hidden = COMPANY_FIELD_KEYS[entity];
  return keys.filter((key) => !hidden.includes(key));
}

/**
 * A value counts as present when it is not null/undefined and, for strings, not
 * blank — a `company: ''` left over from an import is not "business details".
 */
function hasValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
}

/**
 * True when the record carries a value in a key the org's setting hides — the
 * caller then renders the muted read-only "Business details (hidden by your
 * settings)" row. Nothing is silently invisible; that is the whole point.
 */
export function hasHiddenCompanyData(
  record: Record<string, unknown>,
  t: CustomerType | undefined | null,
  entity: CompanyFieldEntity
): boolean {
  if (!hidesCompanyFields(t)) return false;
  if (!record) return false;
  return COMPANY_FIELD_KEYS[entity].some((key) => hasValue(record[key]));
}

/**
 * The subset of hidden keys this record actually has values for, in registry
 * order — what the collapsible row iterates so it never renders an empty label.
 */
export function hiddenCompanyFieldsWithValues(
  record: Record<string, unknown>,
  t: CustomerType | undefined | null,
  entity: CompanyFieldEntity
): string[] {
  if (!hidesCompanyFields(t) || !record) return [];
  return COMPANY_FIELD_KEYS[entity].filter((key) => hasValue(record[key]));
}
